import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Building2 } from "lucide-react";
import { api, tl, dateTR, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const KPI = ({ label, value, accent }) => (
  <div className="border border-white/8 bg-[#0a0a0a] p-5">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">{label}</div>
    <div className={`mt-2 font-display text-2xl md:text-3xl ${accent ? "text-[#d4af37]" : ""}`}>{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">{label}</div>
    {children}
  </label>
);

const KCInputStyle = () => (
  <style>{`.kc-input{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.12);padding:10px 12px;color:white;outline:none}.kc-input:focus{border-color:#d4af37}`}</style>
);

const ApartmentTab = ({ buildingId }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { daire_no: "", owner_name: "", contract_total: 0, paid_amount: 0, installment_count: 0, next_payment_date: "", note: "" };
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);

  const load = () => api.get(`/buildings/${buildingId}/apartments`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, [buildingId]);

  const startEdit = (a) => { setEdit(a.id); setForm({ ...a, next_payment_date: a.next_payment_date || "" }); setOpen(true); };
  const startNew = () => { setEdit(null); setForm(empty); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.daire_no || !form.owner_name) return toast.error("Daire no ve isim zorunlu");
    const payload = { ...form, contract_total: Number(form.contract_total) || 0, paid_amount: Number(form.paid_amount) || 0, installment_count: Number(form.installment_count) || 0, next_payment_date: form.next_payment_date || null };
    try {
      if (edit) await api.put(`/apartments/${edit}`, payload);
      else await api.post(`/buildings/${buildingId}/apartments`, payload);
      toast.success(edit ? "Güncellendi" : "Daire eklendi");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Daire silinecek")) return;
    await api.delete(`/apartments/${id}`); load();
  };

  return (
    <div data-testid="tab-apartments">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-2xl tracking-tight">Daire / Gelir Listesi</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={startNew} data-testid="apartment-add" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-4 py-2.5 font-display uppercase tracking-[0.18em] text-xs transition">
              <Plus className="h-4 w-4" /> Daire Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{edit ? "Daire Düzenle" : "Daire Ekle"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Daire No *"><input data-testid="apartment-input-no" value={form.daire_no} onChange={(e)=>setForm({...form,daire_no:e.target.value})} className="kc-input" required /></Field>
                <Field label="İsim Soyisim *"><input data-testid="apartment-input-owner" value={form.owner_name} onChange={(e)=>setForm({...form,owner_name:e.target.value})} className="kc-input" required /></Field>
                <Field label="Sözleşme Tutarı (₺)"><input type="number" step="0.01" value={form.contract_total} onChange={(e)=>setForm({...form,contract_total:e.target.value})} className="kc-input" /></Field>
                <Field label="Alınan Ödeme (₺)"><input type="number" step="0.01" value={form.paid_amount} onChange={(e)=>setForm({...form,paid_amount:e.target.value})} className="kc-input" /></Field>
                <Field label="Taksit Sayısı"><input type="number" value={form.installment_count} onChange={(e)=>setForm({...form,installment_count:e.target.value})} className="kc-input" /></Field>
                <Field label="Gelecek Ödeme Tarihi"><input type="date" value={form.next_payment_date || ""} onChange={(e)=>setForm({...form,next_payment_date:e.target.value})} className="kc-input" /></Field>
              </div>
              <Field label="Not"><textarea rows={2} value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})} className="kc-input resize-none" /></Field>
              <button data-testid="apartment-save" className="w-full bg-[#d4af37] text-[#121212] hover:bg-white py-3 font-display uppercase tracking-[0.2em] text-xs transition">Kaydet</button>
            </form>
            <KCInputStyle />
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto border border-white/8">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white/55 font-mono text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left p-3">Daire</th>
              <th className="text-left p-3">İsim</th>
              <th className="text-right p-3">Sözleşme</th>
              <th className="text-right p-3">Alınan</th>
              <th className="text-right p-3">Kalan</th>
              <th className="text-center p-3">Taksit</th>
              <th className="text-left p-3">Gelecek Ödeme</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="8" className="p-8 text-center text-white/40">Daire eklenmedi</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="border-t border-white/5 hover:bg-white/5 transition cursor-pointer" onClick={() => startEdit(a)} data-testid={`apt-row-${a.id}`}>
                <td className="p-3 font-mono text-[#d4af37]">{a.daire_no}</td>
                <td className="p-3">{a.owner_name}</td>
                <td className="p-3 text-right">{tl(a.contract_total)}</td>
                <td className="p-3 text-right text-emerald-400/90">{tl(a.paid_amount)}</td>
                <td className="p-3 text-right text-amber-300/90">{tl((a.contract_total||0)-(a.paid_amount||0))}</td>
                <td className="p-3 text-center">{a.installment_count || "-"}</td>
                <td className="p-3">{dateTR(a.next_payment_date)}</td>
                <td className="p-3 text-right"><button onClick={(e)=>{e.stopPropagation();remove(a.id);}} className="text-white/40 hover:text-[#d4af37]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SimpleExpenseTab = ({ buildingId, kind, title, columns, formFields, computeTotal, testIdPrefix }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const initial = formFields.reduce((acc, f) => ({ ...acc, [f.key]: f.type === "number" ? 0 : "" }), {});
  const [form, setForm] = useState(initial);

  const url = `/buildings/${buildingId}/${kind}-expenses`;
  const delUrl = (id) => `/${kind}-expenses/${id}`;

  const load = () => api.get(url).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, [buildingId, kind]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    formFields.forEach((f) => {
      payload[f.key] = f.type === "number" ? (Number(form[f.key]) || 0) : (form[f.key] || "");
    });
    try {
      await api.post(url, payload);
      toast.success("Eklendi");
      setOpen(false); setForm(initial); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const total = items.reduce((acc, i) => acc + (i.total || i.amount || 0), 0);

  return (
    <div data-testid={`tab-${testIdPrefix}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-2xl tracking-tight">{title}</h3>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/45 mt-1">Toplam: <span className="text-[#d4af37]">{tl(total)}</span></div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid={`${testIdPrefix}-add`} className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-4 py-2.5 font-display uppercase tracking-[0.18em] text-xs transition">
              <Plus className="h-4 w-4" /> Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{title} Ekle</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {formFields.map((f) => (
                  <Field key={f.key} label={f.label}>
                    <input
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      step={f.type === "number" ? "0.01" : undefined}
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="kc-input"
                      data-testid={`${testIdPrefix}-input-${f.key}`}
                    />
                  </Field>
                ))}
              </div>
              <button data-testid={`${testIdPrefix}-save`} className="w-full bg-[#d4af37] text-[#121212] hover:bg-white py-3 font-display uppercase tracking-[0.2em] text-xs transition">Kaydet</button>
            </form>
            <KCInputStyle />
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto border border-white/8">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white/55 font-mono text-[10px] uppercase tracking-[0.2em]">
            <tr>{columns.map((c) => <th key={c.key} className={`p-3 ${c.align||"text-left"}`}>{c.label}</th>)}<th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={columns.length+1} className="p-8 text-center text-white/40">Kayıt yok</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-white/5 hover:bg-white/5 transition">
                {columns.map((c) => (
                  <td key={c.key} className={`p-3 ${c.align||"text-left"}`}>{c.render ? c.render(it) : it[c.key]}</td>
                ))}
                <td className="p-3 text-right"><button onClick={async()=>{ await api.delete(delUrl(it.id)); load(); }} className="text-white/40 hover:text-[#d4af37]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function BuildingDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadAll = () => {
    api.get(`/buildings/${id}`).then((r) => setB(r.data));
    api.get(`/buildings/${id}/summary`).then((r) => setSummary(r.data));
  };
  useEffect(() => { loadAll(); }, [id]);

  if (!b) return <div className="text-white/40 font-mono text-xs">Yükleniyor...</div>;

  return (
    <div data-testid="building-detail-page">
      <Link to="/admin/yalitim" className="inline-flex items-center gap-2 text-white/55 hover:text-[#d4af37] transition font-mono text-[11px] uppercase tracking-[0.25em] mb-6"><ArrowLeft className="h-3.5 w-3.5" /> Geri</Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Bina · {b.status}</p>
          <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl md:text-5xl">{b.name}</h1>
          {b.address && <p className="text-white/55 mt-2">{b.address}</p>}
          {b.description && <p className="text-white/70 mt-3 max-w-3xl">{b.description}</p>}
        </div>
        {b.image_url && (
          <div className="lg:col-span-4 aspect-[4/3] overflow-hidden border border-white/8">
            <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="building-kpis">
        <KPI label="Daire" value={summary?.apartment_count ?? "—"} />
        <KPI label="Sözleşme" value={tl(summary?.total_contract || 0)} accent />
        <KPI label="Tahsil" value={tl(summary?.total_paid || 0)} />
        <KPI label="Kalan" value={tl(summary?.total_remaining || 0)} />
        <KPI label="Toplam Gider" value={tl(summary?.total_cost || 0)} />
        <KPI label="Net" value={tl(summary?.net || 0)} accent />
      </div>

      <div className="mt-10">
        <Tabs defaultValue="apartments" className="w-full">
          <TabsList className="bg-[#0a0a0a] border border-white/8 p-1 rounded-none">
            <TabsTrigger value="apartments" data-testid="tablink-apartments" className="rounded-none data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#121212] font-display tracking-[0.15em] uppercase text-xs">Daireler / Gelir</TabsTrigger>
            <TabsTrigger value="materials" data-testid="tablink-materials" className="rounded-none data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#121212] font-display tracking-[0.15em] uppercase text-xs">Malzeme Gideri</TabsTrigger>
            <TabsTrigger value="personnel" data-testid="tablink-personnel" className="rounded-none data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#121212] font-display tracking-[0.15em] uppercase text-xs">Personel Gideri</TabsTrigger>
            <TabsTrigger value="fuel" data-testid="tablink-fuel" className="rounded-none data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#121212] font-display tracking-[0.15em] uppercase text-xs">Yakıt Gideri</TabsTrigger>
          </TabsList>

          <TabsContent value="apartments" className="mt-6"><ApartmentTab buildingId={id} /></TabsContent>

          <TabsContent value="materials" className="mt-6">
            <SimpleExpenseTab
              buildingId={id}
              kind="material"
              testIdPrefix="material"
              title="Malzeme Giderleri"
              formFields={[
                { key: "material_name", label: "Malzeme Adı", type: "text" },
                { key: "quantity", label: "Miktar", type: "number" },
                { key: "unit", label: "Birim", type: "text" },
                { key: "unit_price", label: "Birim Fiyat (₺)", type: "number" },
                { key: "expense_date", label: "Tarih", type: "date" },
                { key: "note", label: "Not", type: "text" },
              ]}
              columns={[
                { key: "material_name", label: "Malzeme" },
                { key: "quantity", label: "Miktar", align: "text-right", render: (i) => `${i.quantity} ${i.unit||""}` },
                { key: "unit_price", label: "Birim", align: "text-right", render: (i) => tl(i.unit_price) },
                { key: "total", label: "Toplam", align: "text-right", render: (i) => <span className="text-[#d4af37]">{tl(i.total)}</span> },
                { key: "expense_date", label: "Tarih", render: (i) => dateTR(i.expense_date) },
              ]}
            />
          </TabsContent>

          <TabsContent value="personnel" className="mt-6">
            <SimpleExpenseTab
              buildingId={id}
              kind="personnel"
              testIdPrefix="personnel-exp"
              title="Personel / İşçilik Giderleri"
              formFields={[
                { key: "personnel_name", label: "Personel Adı", type: "text" },
                { key: "role", label: "Görev", type: "text" },
                { key: "days_worked", label: "Çalışılan Gün", type: "number" },
                { key: "daily_wage", label: "Yevmiye (₺)", type: "number" },
                { key: "expense_date", label: "Tarih", type: "date" },
                { key: "note", label: "Not", type: "text" },
              ]}
              columns={[
                { key: "personnel_name", label: "Personel" },
                { key: "role", label: "Görev" },
                { key: "days_worked", label: "Gün", align: "text-right" },
                { key: "daily_wage", label: "Yevmiye", align: "text-right", render: (i) => tl(i.daily_wage) },
                { key: "total", label: "Toplam", align: "text-right", render: (i) => <span className="text-[#d4af37]">{tl(i.total)}</span> },
                { key: "expense_date", label: "Tarih", render: (i) => dateTR(i.expense_date) },
              ]}
            />
          </TabsContent>

          <TabsContent value="fuel" className="mt-6">
            <SimpleExpenseTab
              buildingId={id}
              kind="fuel"
              testIdPrefix="fuel"
              title="Yakıt Giderleri"
              formFields={[
                { key: "description", label: "Açıklama", type: "text" },
                { key: "liters", label: "Litre", type: "number" },
                { key: "amount", label: "Tutar (₺)", type: "number" },
                { key: "expense_date", label: "Tarih", type: "date" },
                { key: "note", label: "Not", type: "text" },
              ]}
              columns={[
                { key: "description", label: "Açıklama" },
                { key: "liters", label: "Litre", align: "text-right" },
                { key: "amount", label: "Tutar", align: "text-right", render: (i) => <span className="text-[#d4af37]">{tl(i.amount)}</span> },
                { key: "expense_date", label: "Tarih", render: (i) => dateTR(i.expense_date) },
              ]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
